import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Maximize, Minimize } from 'lucide-react';
import { useMediaPlayer } from '@/presentation/hook/useMediaPlayer';
import { MediaPlayerControls } from '@/presentation/component/course/MediaPlayerControls';
import { MediaErrorNotice } from '@/presentation/component/course/MediaErrorNotice';
import { mediaControlButtonClass } from '@/presentation/component/course/mediaPlayerStyles';

interface VideoPlayerProps {
  videoUrl: string;
  initialResumePercent?: number;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}

export const VideoPlayer = ({
  videoUrl,
  initialResumePercent,
  onProgress,
  onComplete,
}: VideoPlayerProps) => {
  const { t } = useTranslation();
  const player = useMediaPlayer<HTMLVideoElement>({
    src: videoUrl,
    initialResumePercent,
    onProgress,
    onComplete,
  });
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    const container = player.mediaRef.current?.parentElement;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (player.hasError) {
    return <MediaErrorNotice />;
  }

  return (
    <div
      className="relative bg-[var(--color-ink)] rounded-lg overflow-hidden group"
      data-testid="video-player"
    >
      <video
        ref={player.mediaRef}
        src={videoUrl}
        className="w-full h-auto"
        data-testid="video-element"
      />

      <button
        onClick={player.togglePlay}
        className="absolute inset-0 flex items-center justify-center"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="video-surface"
      >
        {player.isPlaying ? null : (
          <span className="inline-flex bg-[var(--color-player-scrim)] backdrop-blur-sm rounded-full p-6 text-[var(--color-player-ink)] transition-transform transform hover:scale-110">
            <Play className="w-12 h-12" />
          </span>
        )}
      </button>

      <div className="absolute bottom-0 left-0 right-0 bg-[var(--color-player-scrim)] backdrop-blur-sm px-3 py-2 text-[var(--color-player-ink)] transform translate-y-0 sm:translate-y-full sm:group-hover:translate-y-0 focus-within:translate-y-0 transition-transform duration-300">
        <MediaPlayerControls player={player}>
          <button
            onClick={toggleFullscreen}
            className={mediaControlButtonClass}
            aria-label={
              isFullscreen ? t('mediaPlayer.exitFullscreen') : t('mediaPlayer.fullscreen')
            }
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
        </MediaPlayerControls>
      </div>
    </div>
  );
};
